<template>
  <div class="home">
    <aside class="sidebar">
      <loading-spinner v-if="isLoading" />
      <prototype-tile-component
        v-else
        v-for="(item, index) in prototypes"
        :key="index"
        :prototype="item"
        :selected-index="selected"
        :is-selected="selected === index"
        :is-expanded="expanded === index"
        :sub-mass-notif-index="subMassNotifSelected"
        :sub-notice-index="subNoticeSelected"
        :sub-invitation-index="subInvitationSelected"
        @select="handleSelected(index)"
        @select-mass-notif-child="handleSubMassNotifSelected"
        @select-notice-child="handleSubNoticeSelected"
        @select-invitation-child="handleSubInvitationSelected"
      />
    </aside>
    <div v-if="selectedPrototype && !isLoading" class="preview">
      <prototype-component :prototype="selectedPrototype" />
    </div>
  </div>
</template>

<script lang="ts">
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import PrototypeComponent from "@/components/PrototypeComponent.vue";
import PrototypeTileComponent from "@/components/PrototypeTileComponent.vue";
import { fetchPrototypes } from "@/services/prototypes";
import type { Prototype } from "@/types/prototype";
import {
  INDIVIDUAL_NOTICE_INDEX,
  INVITATIONS_INDEX,
  MASS_NOTIFICATION_INDEX,
} from "@/types/prototype";
import { defineComponent } from "vue";

export default defineComponent({
  name: "HomeView",
  components: {
    LoadingSpinner,
    PrototypeComponent,
    PrototypeTileComponent,
  },
  data() {
    return {
      prototypes: [] as Prototype[],
      selected: 0,
      expanded: -1,
      subNoticeSelected: -1,
      subMassNotifSelected: -1,
      subInvitationSelected: -1,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.prototypes.length === 0;
    },
    selectedPrototype(): Prototype | undefined {
      const item = this.prototypes[this.selected];
      if (!item) return undefined;

      if (this.selected === MASS_NOTIFICATION_INDEX) {
        if (this.subMassNotifSelected === -1) return item;
        return item.items?.[this.subMassNotifSelected];
      }

      if (this.selected === INDIVIDUAL_NOTICE_INDEX) {
        if (this.subNoticeSelected === -1) return item;
        return item.items?.[this.subNoticeSelected];
      }

      if (this.selected === INVITATIONS_INDEX) {
        if (this.subInvitationSelected === -1) return item;
        return item.items?.[this.subInvitationSelected];
      }

      return item;
    },
  },
  watch: {
    selected(index: number) {
      this.ensureInvitationDefault(index);
    },
    prototypes() {
      this.ensureInvitationDefault(this.selected);
    },
  },
  mounted() {
    fetchPrototypes()
      .then((data) => {
        this.prototypes = data;
      })
      .catch(() => {
        // Keep spinner visible when remote config fails to load.
      });
  },
  methods: {
    ensureInvitationDefault(index: number) {
      if (
        index === INVITATIONS_INDEX &&
        this.subInvitationSelected === -1 &&
        this.prototypes[INVITATIONS_INDEX]?.items?.length
      ) {
        this.subInvitationSelected = 0;
      }
    },
    resetSubSelections() {
      this.subMassNotifSelected = -1;
      this.subNoticeSelected = -1;
      this.subInvitationSelected = -1;
    },
    handleSelected(index: number) {
      const hasChildren = !!this.prototypes[index]?.items?.length;

      if (hasChildren && this.selected === index && this.expanded === index) {
        this.expanded = -1;
        this.resetSubSelections();
        return;
      }

      this.selected = index;
      this.expanded = hasChildren ? index : -1;
      this.resetSubSelections();
      this.ensureInvitationDefault(index);
    },
    handleSubNoticeSelected(subindex: number) {
      this.subNoticeSelected = subindex;
      this.subMassNotifSelected = -1;
      this.subInvitationSelected = -1;
    },
    handleSubMassNotifSelected(subindex: number) {
      this.subMassNotifSelected = subindex;
      this.subNoticeSelected = -1;
      this.subInvitationSelected = -1;
    },
    handleSubInvitationSelected(subindex: number) {
      this.subInvitationSelected = subindex;
      this.subNoticeSelected = -1;
      this.subMassNotifSelected = -1;
    },
  },
});
</script>

<style scoped>
.home {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.sidebar {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  border-right: 1px solid #e0e0e0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 16px 24px;
  display: flex;
  flex-direction: column;
  z-index: 1;
}

.preview {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--sidebar-width);
  overflow: hidden;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--prototype-padding-y) var(--prototype-padding-x);
  pointer-events: none;
}

.preview :deep(.prototype) {
  max-height: 100%;
  pointer-events: auto;
}
</style>
